import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send, Bot, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const NEON = "#22C55E";
const NEON_HI = "#4ADE80";
const LINE = "rgba(255,255,255,0.10)";
const PANEL = "#0F0F13";
const DIM = "rgba(255,255,255,0.7)";
const FADE = "rgba(255,255,255,0.4)";

// Some SHOWCASE companies don't have their own tenant record in Mongo.
// For those, we route the demo bot through the `plugitai` tenant (which has
// a full sales prompt configured) and tell the AI which company it's
// role-playing via the opening turn so it stays on-persona.
const BOT_SLUG_MAP = {
  lonestar: "lonestar",   // real tenant
  plugitai: "plugitai",   // real tenant
  // everything else falls back to plugitai
};

function botSlugFor(company) {
  return BOT_SLUG_MAP[company.slug] || "plugitai";
}

function newSessionId() {
  return "hub-" + Math.random().toString(36).slice(2, 12) + "-" + Date.now();
}

export default function BotLauncherDrawer({ company, onClose }) {
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const bootedRef = useRef(false);
  const slug = botSlugFor(company);
  const usingFallback = slug !== company.slug;

  // Priming turn — tells the AI which company it's speaking for so the
  // fallback tenant (plugitai) can convincingly role-play any showcase brand.
  useEffect(() => {
    if (bootedRef.current) return; // guard against React StrictMode double-invoke
    bootedRef.current = true;
    const primer = usingFallback
      ? `You're now the SalesBot for ${company.name} (${company.industry}). Location: ${company.location}. Introduce yourself in one short sentence as their AI and ask the visitor how you can help. Stay in character as ${company.name} for the rest of this conversation.`
      : `Introduce yourself in one short sentence and ask the visitor how you can help.`;
    setMessages([
      { role: "system_hint", body: `Launching BotProviderAI for ${company.name}…` },
    ]);
    (async () => {
      setSending(true);
      try {
        const { data } = await axios.post(`${API}/public/chat`, {
          tenant_slug: slug,
          session_id: sessionId,
          message: primer,
          visitor_name: "Kit (operator)",
        });
        const clean = String(data.reply || "").replace(/\[SIGNAL:[A-Z_]+\]/g, "").trim();
        setMessages([{ role: "ai", body: clean }]);
        setError(""); // clear any stale error on success
      } catch (e) {
        setError("Bot failed to boot. Try again in a moment.");
      } finally {
        setSending(false);
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    })();
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "visitor", body: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/chat`, {
        tenant_slug: slug,
        session_id: sessionId,
        message: text,
        visitor_name: "Kit (operator)",
      });
      const clean = String(data.reply || "").replace(/\[SIGNAL:[A-Z_]+\]/g, "").trim();
      setMessages((m) => [...m, { role: "ai", body: clean }]);
    } catch (err) {
      setError("Bot is temporarily unavailable. Try again.");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="bot-launcher-overlay">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        data-testid="bot-launcher-backdrop"
      />

      {/* Drawer */}
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[440px] md:w-[480px] flex flex-col animate-slideIn"
        style={{
          background: "#08080B",
          borderLeft: `1px solid ${LINE}`,
          boxShadow: `-20px 0 60px -20px rgba(0,0,0,0.7), inset 4px 0 30px -8px ${NEON}22`,
        }}
        data-testid="bot-launcher-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: LINE }}>
          <div className="flex items-center gap-3">
            <div className="relative w-9 h-9 rounded-md flex items-center justify-center" style={{ background: PANEL, border: `1px solid ${NEON}55` }}>
              <Bot size={18} style={{ color: NEON }} />
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: NEON, boxShadow: `0 0 10px ${NEON}` }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: FADE }}>
                BotProviderAI · Live
              </div>
              <div className="font-black text-sm leading-tight text-white" data-testid="bot-launcher-company">
                {company.name}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-white/5"
            style={{ color: DIM }}
            data-testid="bot-launcher-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Fallback note */}
        {usingFallback && (
          <div className="px-5 py-2 text-[10px] uppercase tracking-[0.22em] border-b flex items-center gap-2"
               style={{ background: "rgba(245,184,0,0.06)", borderColor: LINE, color: "rgba(245,184,0,0.9)" }}>
            <Sparkles size={12} />
            Demo mode · role-playing {company.name} via PlugitAI engine
          </div>
        )}

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="bot-launcher-messages">
          {messages.map((m, i) => (
            <Bubble key={i} message={m} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-xs" style={{ color: FADE }}>
              <Loader2 size={13} className="animate-spin" style={{ color: NEON }} />
              <span>Thinking…</span>
            </div>
          )}
          {error && (
            <div className="text-xs rounded-md px-3 py-2 border"
                 style={{ background: "rgba(220,38,38,0.08)", borderColor: "rgba(220,38,38,0.4)", color: "#FCA5A5" }}
                 data-testid="bot-launcher-error">
              {error}
            </div>
          )}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t px-4 py-3" style={{ borderColor: LINE }}>
          <div className="flex items-center gap-2 rounded-md px-3 py-2"
               style={{ background: PANEL, border: `1px solid ${LINE}` }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Message ${company.name}'s bot…`}
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder-white/30"
              disabled={sending}
              data-testid="bot-launcher-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="w-9 h-9 rounded-md inline-flex items-center justify-center transition-all"
              style={{
                background: sending || !input.trim() ? "rgba(255,255,255,0.06)" : NEON,
                color: sending || !input.trim() ? FADE : "#0A0A0C",
              }}
              data-testid="bot-launcher-send"
            >
              <Send size={14} />
            </button>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.22em]" style={{ color: FADE }}>
            Session · {sessionId.slice(0, 14)}…  ·  Powered by Nariai
          </div>
        </form>

        <style>{`
          @keyframes slideIn { from { transform: translateX(100%);} to { transform: translateX(0);} }
          .animate-slideIn { animation: slideIn 260ms cubic-bezier(0.16,1,0.3,1) both; }
        `}</style>
      </aside>
    </div>
  );
}

function Bubble({ message }) {
  const { role, body } = message;
  if (role === "system_hint") {
    return (
      <div className="text-[10px] uppercase tracking-[0.24em] text-center py-2" style={{ color: FADE }}>
        {body}
      </div>
    );
  }
  const isVisitor = role === "visitor";
  return (
    <div className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
      <div
        className="max-w-[86%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed whitespace-pre-wrap"
        style={{
          background: isVisitor ? NEON : PANEL,
          color: isVisitor ? "#0A0A0C" : "#FFFFFF",
          border: isVisitor ? "none" : `1px solid ${LINE}`,
          fontWeight: isVisitor ? 700 : 500,
        }}
        data-testid={isVisitor ? "bot-msg-visitor" : "bot-msg-ai"}
      >
        {body}
      </div>
    </div>
  );
}
